/**
 * /wireline-slickline/ai/:id — one AI draft on its own page
 * (GET /wireline-slickline/ai/digests/:id, fieldiq-api#1362). WS-6's draft page,
 * for this vertical. The back arrow returns to the register with its filters.
 *
 * Panels stack full width: the deterministic figures first (the API's, in
 * full), then the AI draft — "Draft — awaiting your approval" with Approve,
 * Edit-then-approve and Dismiss while the run is pending, the approved text
 * once a person has approved it. A run whose narrative is absent still shows
 * its figures and says the narrative is absent, with the API's reason.
 */
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { useWlAiDigest } from '../../../hooks/useWlAi';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { formatInstant } from '../../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { wlDay } from '../../../lib/wireline-slickline-jobs';
import { canUseWlAi, wlAiAgentLabel, wlAiOutcomeSkin, wlAiScopeText } from '../../../lib/wl-ai';
import { useBackToList } from '../../downhole/recordPage';
import { isNotFound, WL_EYEBROW, WlBadge, WlLoading } from '../WlBits';
import { RunFigures, WlAiDraftPanel } from './WlAiBits';

const LIST = '/wireline-slickline/ai';

const strOf = (t: Record<string, unknown>, k: string): string | null => (typeof t[k] === 'string' && t[k] ? (t[k] as string) : null);

export default function WlAiDraftDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList(LIST);
  const { user } = useAuth();
  const allowed = canUseWlAi(user?.role);
  const tz = useTenantTimeZone();
  const query = useWlAiDigest(id, allowed);
  const run = query.data ?? null;
  const failure = queryLoadFailure(query);
  const notFound = !run && !query.isLoading && isNotFound(queryFailureError(query));
  const t = run?.triggerData ?? {};
  const ticketId = strOf(t, 'ticketId');
  const sheetId = strOf(t, 'sheetId');
  const reportId = strOf(t, 'reportId');
  const jobId = strOf(t, 'jobId');

  return (
    <div data-testid="wl-ai-draft-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={`${WL_EYEBROW} · AI draft`}
        title={run ? wlAiAgentLabel(run.agent) : query.isLoading && allowed ? 'Loading…' : 'AI draft'}
        onBack={back}
        backLabel="Back to AI drafts"
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4" data-testid="wl-ai-draft-detail">
          {!allowed ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text" data-testid="wl-ai-not-allowed">
              AI drafts are for admins, office staff and supervisors.
            </p>
          ) : !run && query.isLoading ? (
            <WlLoading label="Loading the draft" />
          ) : notFound ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text" data-testid="wl-ai-not-found">
              This draft was not found.
            </p>
          ) : !run ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? undefined} subject="this draft" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <WlBadge skin={wlAiOutcomeSkin(run.outcome)} value={run.outcome} />
                <span className="text-fieldiq-text">{wlAiScopeText(String(run.agent), run.triggerData, wlDay)}</span>
                <span className="text-fieldiq-muted">Run {formatInstant(run.createdAt, tz) ?? '—'}</span>
                {run.reviewedAt && <span className="text-fieldiq-muted">· Reviewed {formatInstant(run.reviewedAt, tz) ?? '—'}</span>}
                {sheetId && (
                  <Link to={`/wireline-slickline/sheets/${sheetId}`} className="font-bold text-fieldiq-text underline" data-testid="wl-ai-day-sheet-link">
                    Open the day sheet
                  </Link>
                )}
                {reportId && (
                  <Link to={`/wireline-slickline/reports/${reportId}`} className="font-bold text-fieldiq-text underline" data-testid="wl-ai-report-open-link">
                    Open the report
                  </Link>
                )}
                {ticketId && (
                  <Link to={`/wireline-slickline/tickets/${ticketId}`} className="font-bold text-fieldiq-text underline" data-testid="wl-ai-ticket-link">
                    Open the ticket
                  </Link>
                )}
                {jobId && (
                  <Link to={`/wireline-slickline/jobs/${jobId}`} className="font-bold text-fieldiq-text underline" data-testid="wl-ai-job-link">
                    Open the job
                  </Link>
                )}
              </div>
              {run.outcome === 'superseded' && (
                <p role="status" className="text-xs text-fieldiq-muted">A newer run of this draft replaced this one, so it can no longer be approved.</p>
              )}
              <RunFigures agent={String(run.agent)} data={run.data} />
              <WlAiDraftPanel
                runId={run.agentRunId}
                outcome={run.outcome}
                draft={run.draft}
                approved={run.approved}
                approvedEdited={run.approvedEdited}
                aiError={run.aiError}
                canReview={allowed}
              />
            </>
          )}
        </div>
      </section>
    </div>
  );
}
